// src/features/infoscreens/pages/InfoscreenShowPage/components/InfoscreenTopBar.jsx
import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Logout as LogoutIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { parseUTCToLocalTimezone, getDateLocale } from '../../../../../i18n/LocalizedDateDistance';
import { capitalizeFirst } from '../../../../../utils/formatters/capitalize';

export default function InfoscreenTopBar({
  infoscreenData,
  statusData,
  currentTime,
  isToolbarVisible,
  autoScrollEnabled,
  canAutoScroll = true,
  isFullscreen,
  onMouseEnter,
  onMouseLeave,
  onToggleAutoScroll,
  onToggleFullscreen,
  onLogout,
}) {
  const { t, i18n } = useTranslation(['infoscreens', 'tasks', 'common']);

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        p: 3,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* Left side - Infoscreen name and last updated */}
      <Box
        sx={{
          opacity: isToolbarVisible ? 0 : 1,
          transition: 'opacity 0.3s ease',
          userSelect: 'none',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          {capitalizeFirst(infoscreenData.name)}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {statusData?.generated_at ? (
            <>
              {t('infoscreens:status.last_updated')}:{' '}
              {format(
                parseUTCToLocalTimezone(statusData.generated_at, i18n.language),
                'HH:mm:ss'
              )}
            </>
          ) : (
            <>{' '}</>
          )}
        </Typography>
      </Box>

      {/* Right side - Current time and date */}
      <Box
        textAlign="right"
        sx={{
          opacity: isToolbarVisible ? 0 : 1,
          transition: 'opacity 0.3s ease',
          userSelect: 'none',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          {format(currentTime, 'HH:mm:ss')}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {capitalizeFirst(
            format(currentTime, 'EEEE, MMMM d, yyyy', {
              locale: getDateLocale(i18n.language),
            })
          )}
        </Typography>
      </Box>

      {/* Toolbar - appears on hover */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: 12,
          transform: 'translate(-0%, -50%)',
          display: 'flex',
          gap: 1,
          opacity: isToolbarVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: isToolbarVisible ? 'auto' : 'none',
        }}
      >
        {canAutoScroll && (
          <Tooltip title={autoScrollEnabled ? 'Pause Auto-scroll' : 'Resume Auto-scroll'}>
            <IconButton
              onClick={onToggleAutoScroll}
              sx={{
                color: 'primary.contrastText',
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
              }}
            >
              {autoScrollEnabled ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
          <IconButton
            onClick={onToggleFullscreen}
            sx={{
              color: 'primary.contrastText',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Logout">
          <IconButton
            onClick={onLogout}
            sx={{
              color: 'primary.contrastText',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}